import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Moment */
import moment from "moment"
import "moment/locale/ru"

/* Icons */
import { LoadingOutlined } from "@ant-design/icons"

/* REST API */
import { details } from "../../api/Organization"

/* Constants */
import { env } from "../../constants"

/* Components */
import { Avatar, Icon } from "../../components"

/* Helpers */
import { utils } from "../../helpers"

/* Shared */
import { organizationIcon } from "./shared"

/* Tabs */
import InfoTab from "./InfoTab"
import SettingsTab from "./SettingsTab"
import SubscriptionTab from "./SubscriptionTab"
import TeamTab from "./TeamTab"
import ServicesTab from "./ServicesTab"
import BookingsTab from "./BookingsTab"
import ReviewsTab from "./ReviewsTab"
import FlyerTab from "./FlyerTab"

moment.locale("ru")
Modal.setAppElement("#root")


/* The tabs of the card. Everything the point is managed with lives in one of them — the panel
   has no separate pages for the editing and for the team any more */
const TABS = [
    { key: "info", text: "Информация" },
    { key: "settings", text: "Настройки" },
    { key: "subscription", text: "Подписка" },
    { key: "team", text: "Команда" },
    { key: "services", text: "Услуги" },
    { key: "bookings", text: "Записи" },
    { key: "reviews", text: "Отзывы" },
    { key: "flyer", text: "Рефлет" }
]


/* Widget Details Modal — the card of one point, a panel which slides in from the right.
   It is opened by the reference: this._details.open(item), so the list itself keeps
   nothing about it in its own state */
class DetailsModal extends Component {

    constructor() {
        super()

        this.state = {

            visible: false,

            /* The row of the list — it is drawn at once, while the card is still loading */
            organization: null,

            status: "loading",
            data: null,

            tab: "info"
        }
    }

    /* Open the card of the point */
    open = organization => {
        this.setState({ visible: true, organization, tab: "info", status: "loading", data: null }, () => this.load())
    }

    /* Close the card */
    close = () => {
        this.setState({ visible: false, organization: null, data: null })
    }

    /* Load the card of the point */
    load = () => {

        /* Fields */
        const { organization } = this.state

        /* Check Data */
        if (!organization) {
            return
        }

        details({ id: organization.id }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data })
            }
            else {
                this.setState({ status: "error" })
            }
        }).catch(() => this.setState({ status: "error" }))
    }

    /* A tab changed something — the card reads itself again and the list behind it too */
    reload = () => {

        const { onChanged } = this.props

        this.load()

        if (onChanged) {
            onChanged()
        }
    }

    /* Turn the point on or off — the list does it, the card waits and redraws itself */
    toggle = card => {

        const { onStatus } = this.props
        const result = onStatus(card)

        if (result && result.then) {
            result.then(() => this.load())
        }
    }

    /* The head of the card — the photo, the name and the tabs */
    _head = () => {

        /* Fields */
        const { organization, data, tab } = this.state

        const card = data ? data.organization : organization
        const image = card.image ? `${env.mediapoint}organizations/${card.image}` : null
        const subscribed = card.subscribedUntil && moment(card.subscribedUntil).isAfter(moment())

        return (
            <div className="organization-details-head">

                <div className="organization-details-head-top">

                    <Avatar className="organization-details-avatar" src={image} alt={card.name}>
                        {organizationIcon()}
                    </Avatar>

                    <div className="organization-details-title">

                        <div className="organization-details-name">{utils.check(card.name, "-")}</div>

                        <div className="organization-details-subtitle">
                            <span>#{card.id}</span>
                            {card.city && <span>{card.city}</span>}
                            <span>{card.serviceMode === "QUEUE_BASED" ? "Очередь" : "Запись по времени"}</span>
                        </div>

                        <div className="organization-details-tags">
                            {parseInt(card.visible) === 1 ? <div className="tag green">Включен</div> : <div className="tag red">Отключен</div>}
                            {subscribed
                                ? <div className="tag green">Оплачен до {moment(card.subscribedUntil).format("DD.MM.YYYY")}</div>
                                : <div className="tag grey">Без подписки</div>
                            }
                            {card.subscriptionPrice ? <div className="tag orange">{utils.convertor(card.subscriptionPrice, "₸", 1)}/мес</div> : null}
                        </div>

                    </div>

                    <div onClick={this.close} className="organization-details-close">
                        <Icon name="close" size={11} width={2.2} />
                    </div>

                </div>

                <div className="organization-details-tabs">
                    {TABS.map(item =>
                        <div
                            key={item.key}
                            onClick={() => this.setState({ tab: item.key })}
                            className={`organization-details-tab ${tab === item.key ? "active" : ""}`}
                        >
                            {item.text}
                        </div>
                    )}
                </div>

            </div>
        )
    }

    /* The content of the open tab */
    _content = () => {

        /* Fields */
        const { status, tab, data } = this.state
        const { onQr, onRemove } = this.props

        if (status === "loading") {
            return (
                <div className="organization-details-loading">
                    <LoadingOutlined />
                </div>
            )
        }

        if (status !== "ok" || !data) {
            return (
                <div className="organization-details-loading">
                    <Icon name="alert" size={38} width={1.5} />
                    <p>Не удалось загрузить данные</p>
                    <div onClick={() => this.setState({ status: "loading" }, () => this.load())} className="organization-details-button">Повторить</div>
                </div>
            )
        }

        if (tab === "settings") {
            return <SettingsTab data={data} reload={this.reload} onRemove={onRemove} />
        }

        if (tab === "subscription") {
            return <SubscriptionTab data={data} reload={this.reload} />
        }

        if (tab === "team") {
            return <TeamTab data={data} reload={this.reload} />
        }

        if (tab === "services") {
            return <ServicesTab data={data} reload={this.reload} />
        }

        if (tab === "bookings") {
            return <BookingsTab data={data} />
        }

        if (tab === "reviews") {
            return <ReviewsTab data={data} />
        }

        if (tab === "flyer") {
            return <FlyerTab data={data} />
        }

        return <InfoTab data={data} onQr={onQr} onToggle={this.toggle} />
    }

    render = () => {

        /* Fields */
        const { visible, organization } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={this.close}
                className="organization-details-modal"
                overlayClassName="organization-details-overlay"
                closeTimeoutMS={300}
            >
                {organization &&
                    <div className="organization-details">
                        {this._head()}
                        {this._content()}
                    </div>
                }
            </Modal>
        )
    }
}

export default DetailsModal
